/**
 * @file Helpers for storing order-related data in local storage.
 */

import { wvusLogger } from "../observability/logging";
import { LineItem, StoredOrderDetailType } from "../../types";

/**
 * Parses an array of purchase line items to extract the photo URL metadata.
 *
 * @param lineItems - An array of `LineItem` objects to be parsed.
 * @returns The photo URL as a string if found, otherwise `null`.
 */
export function parseLineItemsForPhoto(lineItems: LineItem[]): string | null {
  if (!Array.isArray(lineItems)) return null;

  let photoUrl = null;

  for (const lineItem of lineItems) {
    if (lineItem?.lineMeta?.wvus_product_event_donor_resources) {
      photoUrl = lineItem?.lineMeta?.wvus_product_event_donor_resources;
      break;
    }
  }

  return photoUrl;
}

/**
 * Stores order details in the local storage.
 *
 * @param order - The order object containing order details.
 * @param order.orderId - The unique identifier for the order.
 * @param order.orderRef - The reference number for the order.
 * @param order.billingAddress - The billing address associated with the order.
 * @param order.billingAddress.email - The email address of the customer.
 * @param order.billingAddress.firstName - The first name of the customer.
 * @param order.billingAddress.lastName - The last name of the customer.
 * @param order.lineItems - The line items included in the order.
 * @param order.eventData - Additional event data related to the order.
 * @param order.eventData.PhotoType - The type of photo associated with the event.
 * @param order.eventData.SignupEndDate - The end date for signing up for the event.
 * @param additionalData - Additional data to be stored with the order details.
 *
 * @throws Will throw an error if unable to save order data to local storage.
 */
export const storeOrderDetailLocalStorage = (order: StoredOrderDetailType, additionalData = {}) => {
  const { orderId, orderRef, billingAddress, lineItems, eventData } = order;
  const photo = parseLineItemsForPhoto(lineItems);
  const { email, firstName, lastName } = billingAddress;

  try {
    localStorage.setItem(
      orderId,
      JSON.stringify({
        orderId,
        orderRef,
        email,
        firstName,
        lastName,
        photo,
        photoType: eventData?.PhotoType?.toLowerCase(),
        signUpEndDate: eventData?.SignupEndDate,
        eventData,
        ...additionalData
      })
    );
  } catch (err) {
    const error = new Error("Unable to save order data to localstorage because: " + err.message);
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Updates the order details in local storage for a given order ID.
 *
 * @param orderId - The unique identifier for the order.
 * @param data - The order details to be updated.
 *
 * @throws Will throw an error if unable to save order data to local storage.
 */
export const updateOrderDetailLocalStorage = (orderId: string, data: StoredOrderDetailType) => {
  const order = retrieveOrderDetailLocalStorage(orderId);
  if (order) {
    try {
      localStorage.setItem(
        orderId,
        JSON.stringify({
          ...order,
          ...data
        })
      );
    } catch (err) {
      const error = new Error("Unable to save order data to localstorage because: " + err.message);
      wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    }
  }
};

/**
 * Retrieves the order details from local storage for a given order ID.
 *
 * @param orderId - The unique identifier for the order.
 * @returns The stored order details if found, otherwise null.
 */
export const retrieveOrderDetailLocalStorage = (orderId: string): StoredOrderDetailType | null => {
  try {
    const order = localStorage.getItem(orderId);
    if (!order) return null;

    try {
      return JSON.parse(order);
    } catch (parseErr) {
      const error = new Error(
        "Unable to parse order data from localstorage because: " + parseErr.message
      );
      wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
      return null;
    }
  } catch (err) {
    const error = new Error(
      "Unable to access order data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};
